import React from 'react';
import { X, Search, Loader2, BookOpen } from 'lucide-react';
import type { Game as CollectionGame } from '../../../lib/game/collectionService';
import { useTheme } from '../../../contexts/ThemeContext';

interface SelectGameModalProps {
  isOpen: boolean;
  isLoading: boolean;
  searchTerm: string;
  searchResults: CollectionGame[];
  onClose: () => void;
  onSearchTermChange: (term: string) => void;
  onGameSelect: (game: CollectionGame) => void;
}

const SelectGameModal: React.FC<SelectGameModalProps> = ({
  isOpen,
  isLoading,
  searchTerm,
  searchResults,
  onClose,
  onSearchTermChange,
  onGameSelect,
}) => {
  const { theme } = useTheme();
  if (!isOpen) return null;

  const searchInputBase = "w-full pl-10 pr-3 py-2 border rounded-md leading-5 sm:text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500";
  const searchInputLight = "border-gray-300 bg-white placeholder-gray-500 focus:placeholder-gray-400 text-gray-900";
  const searchInputDark = "dark:border-gray-600 dark:bg-gray-700 dark:placeholder-gray-400 dark:focus:placeholder-gray-300 dark:text-gray-100";
  const finalSearchInputClasses = `${searchInputBase} ${theme === 'dark' ? searchInputDark : searchInputLight}`;
  
  const listItemButtonBase = "w-full flex items-center p-2 text-left rounded-md transition-colors duration-150";
  const listItemButtonLight = "hover:bg-gray-100";
  const listItemButtonDark = "dark:hover:bg-gray-600";
  const finalListItemButtonClasses = `${listItemButtonBase} ${theme === 'dark' ? listItemButtonDark : listItemButtonLight}`;
  
  const listItemTextLight = "text-gray-800";
  const listItemTextDark = "dark:text-gray-100";
  const finalListItemTextClasses = theme === 'dark' ? listItemTextDark : listItemTextLight;

  const listItemSubtextLight = "text-gray-500";
  const listItemSubtextDark = "dark:text-gray-400";
  const finalListItemSubtextClasses = theme === 'dark' ? listItemSubtextDark : listItemSubtextLight;

  return (
    <div className={`fixed inset-0 ${theme === 'dark' ? 'bg-black bg-opacity-75' : 'bg-gray-500 bg-opacity-75'} flex items-center justify-center z-50 p-4`}>
      <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-md w-full max-h-[80vh] flex flex-col`}>
        {/* Cabeçalho */}
        <div className={`p-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center`}>
          <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>Selecionar Jogo</h3>
          <button onClick={onClose} className={`${theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}>
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Barra de Busca */}
        <div className={`p-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Buscar por nome do jogo..."
              value={searchTerm}
              onChange={(e) => onSearchTermChange(e.target.value)}
              className={finalSearchInputClasses}
              autoFocus
            />
            {isLoading && (
              <Loader2 className={`absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-400'} animate-spin`} />
            )}
          </div>
        </div>

        {/* Lista de Resultados */}
        <div className="flex-grow overflow-y-auto p-4">
          {searchResults.length === 0 && !isLoading && searchTerm.length > 1 && (
            <p className={`text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} py-4`}>Nenhum jogo encontrado.</p>
          )}
          {searchResults.length === 0 && !isLoading && searchTerm.length <= 1 && (
             <p className={`text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} py-4`}>Digite pelo menos 2 caracteres para buscar.</p>
          )}
          {searchResults.length > 0 && (
            <ul className="space-y-2">
              {searchResults.map((game) => (
                <li key={game.id}>
                  <button
                    onClick={() => onGameSelect(game)}
                    className={finalListItemButtonClasses}
                  >
                    {game.thumb_url ? (
                      <img
                        src={game.thumb_url.startsWith("http") ? game.thumb_url : (game.image_url || '/placeholder-game.png')}
                        alt={game.name}
                        className="w-10 h-10 rounded object-cover mr-3 flex-shrink-0"
                        onError={(e) => {
                           (e.target as HTMLImageElement).onerror = null;
                           (e.target as HTMLImageElement).src = game.image_url || '/placeholder-game.png';
                         }}
                      />
                    ) : game.image_url ? (
                       <img
                         src={game.image_url}
                         alt={game.name}
                         className="w-10 h-10 rounded object-cover mr-3 flex-shrink-0"
                         onError={(e) => {
                            (e.target as HTMLImageElement).onerror = null;
                            (e.target as HTMLImageElement).src = '/placeholder-game.png';
                          }}
                       />
                     ) : (
                      <div className={`w-10 h-10 rounded flex items-center justify-center mr-3 flex-shrink-0 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}>
                        <BookOpen className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
                      </div>
                    )}
                    <div className="flex-grow">
                      <span className={`block font-medium ${finalListItemTextClasses}`}>{game.name}</span>
                      {game.year && (
                        <span className={`text-sm ${finalListItemSubtextClasses}`}>({game.year})</span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectGameModal; 